// =============================================================================
// INCOMING CALL MODAL
// =============================================================================
// Full-screen modal shown when receiving an incoming call

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCallStore } from '../../stores/callStore.js';
import { useFocusTrap } from '../../hooks/useFocusTrap.js';
import { Phone, Video, X } from '../ui/Icons.js';

// =============================================================================
// COMPONENT
// =============================================================================

export function IncomingCallModal() {
  const { t } = useTranslation();
  const { incomingCall, answerCall, declineCall, isLoading } = useCallStore();

  // Warm ringtone + vibration while a call is incoming.
  //
  // Replaces the bare 440Hz beep with a two-note chime (a perfect fifth, the
  // familiar "ring-ring" warmth) sounded in a phone-like cadence: two short
  // rings, then a pause, repeating. A soft attack/release on the gain keeps it
  // from clicking. Vibration mirrors the same cadence on devices that support
  // it. Both stop the moment the call is answered, declined, or cancelled.
  useEffect(() => {
    if (!incomingCall) {
      return;
    }

    let audioContext: AudioContext | null = null;
    let ringTimer: ReturnType<typeof setInterval> | undefined;
    const scheduled: OscillatorNode[] = [];

    const canVibrate = typeof navigator.vibrate === 'function';

    try {
      audioContext = new AudioContext();
      const ctx = audioContext;

      // One "ring" = the two notes sounded together with a gentle envelope.
      const ring = (startAt: number) => {
        const gain = ctx.createGain();
        gain.connect(ctx.destination);
        gain.gain.setValueAtTime(0, startAt);
        gain.gain.linearRampToValueAtTime(0.18, startAt + 0.04);
        gain.gain.setValueAtTime(0.18, startAt + 0.36);
        gain.gain.linearRampToValueAtTime(0, startAt + 0.4);

        [440, 660].forEach((frequency) => {
          const osc = ctx.createOscillator();
          osc.type = 'sine';
          osc.frequency.value = frequency;
          osc.connect(gain);
          osc.start(startAt);
          osc.stop(startAt + 0.42);
          scheduled.push(osc);
        });
      };

      // Two rings up front, then repeat the pair every 3s for the ring cadence.
      const playCadence = () => {
        const now = ctx.currentTime;
        ring(now);
        ring(now + 0.6);
      };

      playCadence();
      ringTimer = setInterval(playCadence, 3000);

      if (canVibrate) {
        // ring, gap, ring, then long gap - matches the audio cadence.
        navigator.vibrate([400, 200, 400, 2000]);
        const vibrateTimer = setInterval(
          () => navigator.vibrate([400, 200, 400, 2000]),
          3000
        );
        return () => {
          clearInterval(ringTimer);
          clearInterval(vibrateTimer);
          navigator.vibrate(0);
          scheduled.forEach((osc) => {
            try {
              osc.stop();
            } catch {
              /* already stopped */
            }
          });
          void audioContext?.close();
        };
      }

      return () => {
        clearInterval(ringTimer);
        scheduled.forEach((osc) => {
          try {
            osc.stop();
          } catch {
            /* already stopped */
          }
        });
        void audioContext?.close();
      };
    } catch {
      // Web Audio unavailable in this context - fall back to vibration only.
      if (canVibrate) {
        navigator.vibrate([400, 200, 400, 2000]);
        const vibrateTimer = setInterval(
          () => navigator.vibrate([400, 200, 400, 2000]),
          3000
        );
        return () => {
          clearInterval(vibrateTimer);
          navigator.vibrate(0);
        };
      }
    }
  }, [incomingCall]);

  const focusTrapRef = useFocusTrap(!!incomingCall);

  if (!incomingCall) {
    return null;
  }

  const isVideoCall = incomingCall.callType === 'video';

  const handleAnswer = async () => {
    await answerCall();
  };

  const handleDecline = async () => {
    await declineCall();
  };

  return (
    <div ref={focusTrapRef} className="fixed inset-0 z-50 flex items-center justify-center bg-surface-1/95" role="alertdialog" aria-modal="true" aria-label={t('calls.incomingCall', 'Incoming call')}>
      {/* Background animation */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="animate-ping-slow absolute h-64 w-64 rounded-full bg-primary/20" />
          <div
            className="animate-ping-slow absolute h-48 w-48 rounded-full bg-primary/30"
            style={{ animationDelay: '0.5s' }}
          />
          <div
            className="animate-ping-slow absolute h-32 w-32 rounded-full bg-primary/40"
            style={{ animationDelay: '1s' }}
          />
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center px-4 text-center">
        {/* Call type indicator */}
        <div className="mb-6 text-primary">
          {isVideoCall
            ? <Video className="h-10 w-10" />
            : <Phone className="h-10 w-10" />}
        </div>

        {/* Caller avatar */}
        <div className="mb-4 flex h-28 w-28 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary-press text-4xl font-bold text-ink-1 shadow-lg">
          {incomingCall.initiatorName.charAt(0).toUpperCase()}
        </div>

        {/* Caller name */}
        <h2 className="mb-2 text-2xl font-bold text-ink-1">
          {incomingCall.initiatorName}
        </h2>

        {/* Call type text */}
        <p className="mb-2 text-lg text-ink-2">
          {isVideoCall
            ? t('calls.incomingVideo', 'Incoming video call')
            : t('calls.incomingVoice', 'Incoming voice call')}
        </p>

        {/* Conversation name if group */}
        {incomingCall.conversationName && (
          <p className="mb-4 text-sm text-ink-3">
            {incomingCall.conversationName}
          </p>
        )}

        {/* Ringing indicator */}
        <div className="mb-8 flex items-center gap-2 text-ink-3">
          <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-primary" />
          <span>{t('calls.ringing', 'Ringing...')}</span>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-8">
          {/* Decline button */}
          <button
            onClick={handleDecline}
            disabled={isLoading}
            className="flex h-16 w-16 items-center justify-center rounded-full bg-danger text-white shadow-lg transition-all hover:bg-danger/80 hover:scale-110 active:scale-95 disabled:opacity-50"
            aria-label={t('calls.decline', 'Decline')}
          >
            <X className="h-7 w-7" />
          </button>

          {/* Accept button */}
          <button
            onClick={handleAnswer}
            disabled={isLoading}
            className="flex h-20 w-20 items-center justify-center rounded-full bg-success text-white shadow-lg transition-all hover:bg-success/80 hover:scale-110 active:scale-95 disabled:opacity-50"
            aria-label={t('calls.accept', 'Accept')}
          >
            {isVideoCall
              ? <Video className="h-9 w-9" />
              : <Phone className="h-9 w-9" />}
          </button>
        </div>

        {/* Labels */}
        <div className="mt-4 flex items-center gap-12">
          <span className="text-sm text-ink-3">
            {t('calls.decline', 'Decline')}
          </span>
          <span className="text-sm text-ink-3">
            {t('calls.accept', 'Accept')}
          </span>
        </div>

        {/* Loading indicator */}
        {isLoading && (
          <div className="mt-6 flex items-center gap-2 text-ink-3">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <span>{t('calls.connecting', 'Connecting...')}</span>
          </div>
        )}
      </div>

      {/* CSS for custom animation */}
      <style>{`
        @keyframes ping-slow {
          0% {
            transform: scale(1);
            opacity: 0.4;
          }
          50% {
            transform: scale(1.5);
            opacity: 0;
          }
          100% {
            transform: scale(1);
            opacity: 0.4;
          }
        }
        .animate-ping-slow {
          animation: ping-slow 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </div>
  );
}
